import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { useUnits } from '../../../core/api';
import { Link } from 'react-router-dom';
import { BadgePercent, Home, Clock } from 'lucide-react';
import { LoadingSpinner } from '../../../core/components/LoadingSpinner';
import { ProjectCard } from './ProjectCard';

interface SmartCollectionsProps {
    config?: any;
}

export const SmartCollections = ({ config }: SmartCollectionsProps) => {
    const { t } = useTranslation();
    const activeCollection = config?.collectionType || 'best_value'; // configured in admin
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Ideally, we'd pass filters to useUnits based on the collection type
    const { data: units, isLoading } = useUnits({ limit: 6 });

    const collections = {
        best_value: {
            title: t('explore.collections.value.title', 'Best Value for Investors'),
            subtitle: t('explore.collections.value.subtitle', 'High ROI & Flexible Plans'),
            icon: BadgePercent,
            color: 'text-green-600',
            bg: 'bg-green-50',
            searchParams: '?sort=priceAsc'
        },
        family_homes: {
            title: t('explore.collections.family.title', 'Perfect Family Homes'),
            subtitle: t('explore.collections.family.subtitle', 'Spacious units near amenities'),
            icon: Home,
            color: 'text-blue-600',
            bg: 'bg-blue-50',
            searchParams: '?beds=3'
        },
        immediate: {
            title: t('explore.collections.immediate.title', 'Move In Now'),
            subtitle: t('explore.collections.immediate.subtitle', 'Ready for immediate delivery'),
            icon: Clock,
            color: 'text-amber-600',
            bg: 'bg-amber-50',
            searchParams: '?ready=true'
        }
    };

    const currentCollection = collections[activeCollection as keyof typeof collections] || collections.best_value;
    const Icon = currentCollection.icon;

    if (isLoading) return <LoadingSpinner />;

    return (
        <section className="py-8 bg-slate-50">
            <div className="max-w-7xl mx-auto px-4 md:px-8">

                {/* Collection Header */}
                <div className="flex items-center gap-3 mb-6">
                    <div className={`p-2 rounded-lg ${currentCollection.bg}`}>
                        <Icon className={`w-6 h-6 ${currentCollection.color}`} />
                    </div>
                    <div>
                        <h2 className="text-xl font-bold text-slate-900">{currentCollection.title}</h2>
                        <p className="text-sm text-slate-500">{currentCollection.subtitle}</p>
                    </div>
                </div>

                {/* Grid Layout */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 relative z-10">
                    {(units as any[] || []).slice(0, 6).map((unit) => {
                        const cardData = {
                            id: unit.id,
                            title: unit.title || unit.slug,
                            location: unit.project?.title || unit.project?.name || '',
                            specs: {
                                beds: unit.beds || 0,
                                baths: unit.baths || 0,
                                area: unit.area || '—'
                            },
                            price: unit.price,
                            payment: unit.payment || t('common.installments', 'Installments'),
                            status: unit.status || (unit.ready ? t('status.readyToMove') : t('status.available')),
                            image: unit.image || '',
                            deliveryDate: unit.deliveryDate,
                            finishType: unit.finishType
                        };

                        return (
                            <div key={unit.id} className="h-full">
                                <ProjectCard
                                    project={cardData}
                                    linkTo={`/units/${unit.slug || unit.id}`}
                                    className="h-full w-full"
                                    type="Unit"
                                    layout={isMobile ? 'horizontal-mobile' : 'standard'}
                                />
                            </div>
                        );
                    })}
                </div>

                <div className="mt-8 text-center">
                    <Link
                        to={`/search${currentCollection.searchParams}`}
                        className="inline-flex items-center justify-center px-6 py-3 border border-transparent shadow-sm text-sm font-bold rounded-md text-white bg-primary hover:bg-primary/90 transition-colors"
                    >
                        {t('explore.collections.viewAll', 'View All Collection')}
                    </Link>
                </div>
            </div>
        </section>
    );
};
